import {
  formatCompactCurrency,
  formatCurrencyMetricValue,
  resolveCurrencyInputScale,
} from "./formatCompactCurrency";
import { formatNumberValue } from "../../intelligence-briefing-library/utils/formatting/numberFormatter";
import { formatPercentageValue } from "../../intelligence-briefing-library/utils/formatting/percentageFormatter";

const isCurrencyLikeMetric = (
  format?: string,
  unitOfMeasure?: string,
  metricName?: string
): boolean => {
  const formatLower = format?.toLowerCase() || "";
  const uomLower = unitOfMeasure?.toLowerCase() || "";
  const nameLower = metricName?.toLowerCase() || "";
  if (formatLower === "currency" || uomLower === "usd") return true;
  return /\b(revenue|recurring|arr|mrr)\b/i.test(nameLower);
};

/** Compact currency for Hawkeye / KPI metrics: $10k, $1M, $1B. */
export const formatHawkeyeMetricValue = (
  value: number,
  format?: string,
  scale?: string,
  unitOfMeasure?: string,
  metricName?: string,
  metricId?: string
): string => {
  if (Number.isNaN(value) || !Number.isFinite(value)) {
    return "NA";
  }

  const formatLower = format?.toLowerCase() || "";
  const scaleLower = scale?.toLowerCase() || "";
  const uomLower = unitOfMeasure?.toLowerCase() || "";

  if (isCurrencyLikeMetric(format, unitOfMeasure, metricName)) {
    return formatCurrencyMetricValue(value, {
      scale,
      unitOfMeasure,
      metricName,
      metricId,
    });
  }

  if (
    formatLower === "percentage" ||
    uomLower === "%" ||
    formatLower.includes("percent")
  ) {
    return formatPercentageValue(value).formatted;
  }

  if (
    formatLower === "integer" ||
    formatLower === "number" ||
    formatLower === "numeric"
  ) {
    if (
      scaleLower === "millions" ||
      scaleLower === "billions" ||
      scaleLower === "thousands"
    ) {
      const inputScale = resolveCurrencyInputScale(
        scale,
        unitOfMeasure,
        metricName,
        metricId
      );
      return formatCompactCurrency(value, { inputScale });
    }
    return formatNumberValue(value);
  }

  return value.toFixed(2);
};

export const formatMetricDisplayValue = (
  rawValue: string | undefined,
  format?: string,
  scale?: string,
  unitOfMeasure?: string,
  metricName?: string,
  metricId?: string
): string => {
  if (rawValue == null || rawValue.trim() === "") return "NA";

  const normalizedCompact = formatCompactCurrency(rawValue, {
    preserveFormattedStrings: true,
    inputScale: resolveCurrencyInputScale(
      scale,
      unitOfMeasure,
      metricName,
      metricId
    ),
  });

  const parsed = parseFloat(String(rawValue).replace(/[^0-9.-]/g, ""));
  if (!Number.isFinite(parsed)) {
    return isCurrencyLikeMetric(format, unitOfMeasure, metricName)
      ? normalizedCompact
      : rawValue;
  }

  return formatHawkeyeMetricValue(
    parsed,
    format,
    scale,
    unitOfMeasure,
    metricName,
    metricId
  );
};

/**
 * Always On Channel Actual Value display:
 * compact currency when UOM is USD, otherwise raw value + unit (e.g. "95 Score").
 */
export const formatActualValueWithUnit = (
  actualValue: string | undefined,
  unitOfMeasure?: string | null
): string => {
  if (actualValue == null || String(actualValue).trim() === "") return "NA";

  const uom = (unitOfMeasure || "").trim();
  const uomLower = uom.toLowerCase();
  const formatted =
    uomLower && uomLower !== "usd" && !uomLower.includes("dollar")
      ? String(actualValue)
      : formatCompactCurrency(actualValue, { preserveFormattedStrings: true });

  return uom ? `${formatted} ${uom}` : formatted;
};
